// PlaceCard Component - Redesigned for Atmosphere Experience
// Emotionales Design mit 16:9 Bildern, GoodFactor im Zitat-Stil, Atmosphere Badges

import React, { useMemo, useCallback } from 'react';
import {
  Heart,
  Star,
  MapPin,
  Clock,
  Palette,
  Sofa,
  Moon,
  Trees,
  Quote,
  UserCircle,
  MessageCircle,
  Sparkles,
  DoorOpen,
  Hammer,
  History,
  Ear,
  Coffee,
  Lightbulb,
  LucideIcon,
  // GoodWork Icons
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
  Briefcase,
  // GoodWork VibeTag Icons
  Focus,
  Rocket,
  Leaf,
  Sun,
} from 'lucide-react';
import type { Place, MainCategory, GoodWorkCategory, AnyCategory, VibeTag, Realm, WifiQuality, PowerOutletAvailability } from '../../types';
import { MAIN_CATEGORIES, GOODWORK_CATEGORIES, VIBE_TAGS, GOODWORK_VIBE_TAGS, ALL_CATEGORIES } from '../../types';
import { useRealm } from '../../context/RealmContext';

// GoodWork text labels
const WIFI_SHORT_LABELS: Record<WifiQuality, string> = {
  none: '',
  basic: 'WLAN',
  fast: 'Schnelles WLAN',
  excellent: 'Top WLAN',
};
import { AtmosphereBadge } from './AtmosphereBadge';
import { ValouBadges } from '../valou';
import { getPlaceValouAreas } from '../../services/valouService';
import { isPlaceOpenNow } from '../../utils/openingHours';
import { DistanceText } from '../discovery/DistanceBadge';

// Icon mapping for categories and vibe tags
const ICON_MAP: Record<string, LucideIcon> = {
  // GoodLife Categories
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  // GoodWork Categories
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
  // GoodLife Vibe Tags
  UserCircle,
  MessageCircle,
  Sparkles,
  DoorOpen,
  Hammer,
  History,
  Ear,
  Coffee,
  Lightbulb,
  // GoodWork Vibe Tags
  Focus,
  Briefcase,
  Heart,
  Rocket,
  Leaf,
  Sun,
};

// Color mapping for categories - neutral gray for GoodLife, indigo for GoodWork
const CATEGORY_COLORS: Record<AnyCategory, string> = {
  // GoodLife
  urban_creative: 'bg-gray-100 text-gray-800 border-gray-300',
  public_living_room: 'bg-gray-100 text-gray-800 border-gray-300',
  silent_sanctuary: 'bg-gray-100 text-gray-800 border-gray-300',
  analoge_refugien: 'bg-gray-100 text-gray-800 border-gray-300',
  natur_outdoor: 'bg-gray-100 text-gray-800 border-gray-300',
  // GoodWork
  coworking: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  focused_work: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  creative_studio: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  meeting_space: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  maker_space: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  learning_hub: 'bg-indigo-100 text-indigo-800 border-indigo-300',
};

interface PlaceCardProps {
  place: Place;
  isFavorite: boolean;
  onSelect: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
  userLocation?: { latitude: number; longitude: number } | null;
}

// Get GoodWork summary (amenities + price) for card display
function getGoodWorkSummary(place: Place): string[] {
  const items: string[] = [];

  // Amenities - only key highlights
  if (place.amenities?.wifi && place.amenities.wifi !== 'none') {
    items.push(WIFI_SHORT_LABELS[place.amenities.wifi]);
  }
  if (place.amenities?.powerOutlets === 'many') {
    items.push('Viele Steckdosen');
  }
  if (place.amenities?.monitors) items.push('Monitore');
  if (place.amenities?.meetingRooms) items.push('Meeting-Räume');

  // Price indicator
  if (place.pricing?.daily) {
    items.push(`ab ${place.pricing.daily}€/Tag`);
  } else if (place.pricing?.hourly) {
    items.push(`ab ${place.pricing.hourly}€/h`);
  }

  // Drop-in hint
  if (place.pricing?.dropIn) {
    items.push('Drop-in');
  }

  return items.filter(Boolean).slice(0, 4); // Max 4 items
}

// Extract district from address or use district field
function getDistrict(place: Place): string {
  if (place.district) {
    return place.district;
  }
  if (place.address) {
    const parts = place.address.split(',').map(p => p.trim());
    if (parts.length >= 3) {
      const lastPart = parts[parts.length - 1];
      if (!/^\d{5}/.test(lastPart) && lastPart.toLowerCase() !== 'muenchen' && lastPart.toLowerCase() !== 'muenchen') {
        return lastPart;
      }
    }
  }
  return place.city || 'München';
}

// Vibe tag icon mapping (using ICON_MAP)

export const PlaceCard = React.memo(function PlaceCard({
  place,
  isFavorite,
  onSelect,
  onToggleFavorite,
  userLocation,
}: PlaceCardProps) {
  const { isGoodWork } = useRealm();

  // Memoize computed values to prevent recalculation on every render
  const imageUrl = useMemo(
    () => place.images?.[0] || place.imageUrl || '/api/placeholder/800/450',
    [place.images, place.imageUrl]
  );

  const district = useMemo(() => getDistrict(place), [place.district, place.address, place.city]);

  const isOpen = useMemo(
    () => place.openingHours ? isPlaceOpenNow(place.openingHours) : null,
    [place.openingHours]
  );

  const valouAreas = useMemo(() => getPlaceValouAreas(place), [place]);

  // Get category info (realm-aware: GoodWork uses goodworkCategory, GoodLife uses category) - memoized
  const mainCategoryInfo = useMemo(() => {
    const categoryId = isGoodWork ? place.goodworkCategory : place.category;
    return categoryId ? ALL_CATEGORIES.find((c) => c.id === categoryId) : null;
  }, [place.category, place.goodworkCategory, isGoodWork]);

  // Check if place is in both realms
  const isDualRealm = useMemo(
    () => place.realms?.includes('goodlife') && place.realms?.includes('goodwork'),
    [place.realms]
  );

  // Get vibe tag infos (realm-aware: GoodWork uses goodworkVibeTags, GoodLife uses vibeTags) - memoized
  const vibeTagInfos = useMemo(() => {
    const tags = isGoodWork ? place.goodworkVibeTags : place.vibeTags;
    const tagDefinitions = isGoodWork ? GOODWORK_VIBE_TAGS : VIBE_TAGS;
    return tags
      ? tags.slice(0, 3).map((tagId) => tagDefinitions.find((t) => t.id === tagId)).filter(Boolean)
      : [];
  }, [place.vibeTags, place.goodworkVibeTags, isGoodWork]);

  // GoodWork summary (amenities + price) - only for GoodWork places
  const goodWorkSummary = useMemo(
    () => place.realms?.includes('goodwork') ? getGoodWorkSummary(place) : [],
    [place.realms, place.amenities, place.pricing]
  );

  // Memoized event handlers
  const handleCardClick = useCallback(() => onSelect(place), [onSelect, place]);

  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite(place.id);
  }, [onToggleFavorite, place.id]);

  return (
    <div
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm
                 hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300
                 hover:-translate-y-1 hover:ring-1 hover:ring-amber-200/50"
      onClick={handleCardClick}
    >
      {/* Image Container - 16:9 Cinematographic */}
      <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt={place.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'https://placehold.co/800x450/f5e6d3/8b7355?text=GoodPlace';
          }}
        />

        {/* Warmer Gradient Overlay - Golden Hour Effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Subtle warm overlay on hover */}
        <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/5 transition-colors duration-500" />

        {/* Atmosphere Badges - Top Left */}
        {place.atmosphere && place.atmosphere.length > 0 && (
          <div className="absolute top-3 left-3 z-10">
            <AtmosphereBadge atmospheres={place.atmosphere} maxDisplay={2} size="sm" />
          </div>
        )}

        {/* Category Badge - wenn keine Atmosphere */}
        {(!place.atmosphere || place.atmosphere.length === 0) && (
          <div className="absolute top-3 left-3">
            {mainCategoryInfo && (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold
                           backdrop-blur-md shadow-sm border ${CATEGORY_COLORS[mainCategoryInfo.id as AnyCategory] || 'bg-gray-100 text-gray-800 border-gray-300'}`}
              >
                {ICON_MAP[mainCategoryInfo.icon] && React.createElement(ICON_MAP[mainCategoryInfo.icon], { className: 'w-3.5 h-3.5' })}
                <span className="hidden sm:inline">{mainCategoryInfo.label.split(' ')[0]}</span>
              </div>
            )}
          </div>
        )}

        {/* Dual-Realm Badge - Small indicator for places in both realms */}
        {isDualRealm && (
          <div className="absolute top-3 left-3 z-20" style={{ marginLeft: (place.atmosphere?.length ?? 0) > 0 ? '0' : mainCategoryInfo ? '100px' : '0' }}>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium
                          bg-gradient-to-r from-emerald-500/90 to-indigo-500/90 text-white
                          backdrop-blur-md shadow-sm">
              <Heart className="w-3 h-3" />
              <span>+</span>
              <Briefcase className="w-3 h-3" />
            </div>
          </div>
        )}

        {/* Favorite Button - Warmer Style */}
        <button
          className="absolute top-3 right-3 p-2.5 transition-all duration-300
                     hover:scale-110 z-10 rounded-full
                     bg-white/20 backdrop-blur-sm hover:bg-white/40"
          onClick={handleFavoriteClick}
          aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
        >
          <Heart
            className={`w-5 h-5 drop-shadow-lg transition-colors ${
              isFavorite
                ? 'fill-rose-500 text-rose-500'
                : 'fill-white/30 text-white hover:fill-white/50'
            }`}
          />
        </button>

        {/* Status Badge (for pending/rejected) */}
        {place.status !== 'approved' && (
          <div
            className={`absolute bottom-16 right-3 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
              place.status === 'pending'
                ? 'bg-amber-100/90 text-amber-800'
                : 'bg-red-100/90 text-red-800'
            }`}
          >
            {place.status === 'pending' ? 'Ausstehend' : 'Abgelehnt'}
          </div>
        )}

        {/* Name and Tagline over Image - Enhanced Typography */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h3 className="font-bold text-xl leading-tight line-clamp-1 drop-shadow-lg">
            {place.name}
          </h3>
          {place.tagline && (
            <p className="text-sm text-white/90 italic line-clamp-1 mt-1 drop-shadow font-light">
              {place.tagline}
            </p>
          )}
        </div>
      </div>

      {/* Content Section - Redesigned */}
      <div className="p-4">
        {/* GoodFactor Section - Prominent Quote Style */}
        {place.goodFactor && (
          <div className="mb-4 relative">
            <div className="absolute -left-1 top-0 text-amber-300/60">
              <Quote className="w-6 h-6 rotate-180" />
            </div>
            <blockquote className="pl-5 pr-2">
              <p className="text-gray-700 text-sm leading-relaxed line-clamp-2 italic">
                {place.goodFactor}
              </p>
            </blockquote>
          </div>
        )}

        {/* Fallback: Description if no GoodFactor */}
        {!place.goodFactor && place.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
            {place.description}
          </p>
        )}

        {/* GoodWork Summary - Text-based, subtle */}
        {goodWorkSummary.length > 0 && (
          <p className="text-xs text-indigo-600 mb-3">
            {goodWorkSummary.join(' · ')}
          </p>
        )}

        {/* Vibe Tags - Icon Style */}
        {vibeTagInfos.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-gray-600">
            {vibeTagInfos.map((tagInfo, index) => {
              if (!tagInfo) return null;
              const IconComponent = ICON_MAP[tagInfo.icon];
              return (
                <span key={tagInfo.id} className="inline-flex items-center gap-1">
                  {IconComponent && <IconComponent className="w-3.5 h-3.5 text-gray-500" />}
                  <span>{tagInfo.label}</span>
                  {index < vibeTagInfos.length - 1 && (
                    <span className="text-gray-300 ml-1">·</span>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {/* Meta Bar - Cleaner Design */}
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap pt-2 border-t border-gray-100">
          {/* District + Entfernung */}
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-gray-400" />
            <span>{district}</span>
            {userLocation && place.latitude && place.longitude && (
              <>
                <span className="text-gray-300 mx-0.5">·</span>
                <DistanceText
                  userLocation={userLocation}
                  placeLocation={{ latitude: place.latitude, longitude: place.longitude }}
                />
              </>
            )}
          </span>

          {/* Rating */}
          {place.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <span className="font-medium text-gray-700">{place.rating.toFixed(1)}</span>
              {place.reviewCount > 0 && (
                <span className="text-gray-400">({place.reviewCount})</span>
              )}
            </span>
          )}

          {/* Open Status */}
          {isOpen !== null && (
            <span className={`flex items-center gap-1 ${isOpen ? 'text-emerald-600' : 'text-gray-400'}`}>
              <Clock className="w-3.5 h-3.5" />
              {isOpen ? 'Jetzt offen' : 'Geschlossen'}
            </span>
          )}
        </div>

        {/* Valou Badges - wenn vorhanden */}
        {valouAreas.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <ValouBadges areas={valouAreas} maxDisplay={2} size="xs" />
          </div>
        )}
      </div>
    </div>
  );
});

export default PlaceCard;
